import { useState, useEffect } from 'react';
import { api } from '../services/api';
import { getHubSpotContext } from '../utils/hubspotContext';

export const useAuth = () => {
  const [userToken, setUserToken] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentWorkspace, setCurrentWorkspace] = useState(null);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(null);

  useEffect(() => {
    // Listen for authentication failures
    const handleAuthFailed = () => {
      console.log('Authentication failed - clearing tokens and redirecting to login');

      // Clear all auth data
      localStorage.removeItem('slack_user_token');
      localStorage.removeItem('selected_workspace_id');
      setUserToken(null);
      setIsAuthenticated(false);
      setCurrentWorkspace(null);
      setSelectedWorkspaceId(null);

      // Redirect to OAuth/workspace selection
      const hubspotContext = getHubSpotContext();
      let oauthUrl = '/api/slack/oauth';
      const params = new URLSearchParams();

      // The signed handoff token is what binds the Slack connection to the real
      // HubSpot user. hubspotUserId/portalId are passed only as context hints.
      if (hubspotContext?.handoffToken) {
        params.append('st', hubspotContext.handoffToken);
      }
      if (hubspotContext?.userId) {
        params.append('hubspotUserId', hubspotContext.userId);
      }
      if (hubspotContext?.portalId) {
        params.append('hubspotPortalId', hubspotContext.portalId);
      }

      if (params.toString()) {
        oauthUrl += '?' + params.toString();
      }

      window.location.href = oauthUrl;
    };

    window.addEventListener('auth-failed', handleAuthFailed);

    const checkAuth = async () => {
      // Get HubSpot context first
      const hubspotContext = getHubSpotContext();
      const handoffToken = hubspotContext?.handoffToken;

      // First check if token is in URL parameters (from OAuth redirect)
      const urlParams = new URLSearchParams(window.location.search);
      const tokenFromUrl = urlParams.get('token');

      if (tokenFromUrl) {
        localStorage.setItem('slack_user_token', tokenFromUrl);
        setUserToken(tokenFromUrl);
        setIsAuthenticated(true);
        // Clean up URL
        window.history.replaceState({}, document.title, '/');

        // Fetch current workspace info
        try {
          const workspaceResponse = await api.fetchCurrentWorkspace(tokenFromUrl);
          setCurrentWorkspace(workspaceResponse.data);
          localStorage.setItem('selected_workspace_id', workspaceResponse.data.teamId);
          setSelectedWorkspaceId(workspaceResponse.data.teamId);
        } catch (error) {
          console.error('Failed to fetch workspace info:', error);
        }

        setLoading(false);
        return;
      }

      // Check backend for persisted token (with HubSpot user ID if available)
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
        const headers = {
          'Content-Type': 'application/json'
        };

        // The signed handoff token is the trusted proof of identity. Falls back
        // to the session cookie (credentials: include) for reloads after the
        // token has expired.
        if (handoffToken) {
          headers['x-hubspot-handoff'] = handoffToken;
        }

        const url = `${backendUrl}/api/auth/status`;

        const response = await fetch(url, {
          credentials: 'include',
          headers
        });
        const data = await response.json();

        if (data.authenticated && data.token) {
          // Store token from database into localStorage for faster access
          localStorage.setItem('slack_user_token', data.token);
          setUserToken(data.token);
          setIsAuthenticated(true);

          // Fetch current workspace info
          try {
            // Check if there's a saved workspace preference
            const savedWorkspaceId = localStorage.getItem('selected_workspace_id');
            const workspaceResponse = await api.fetchCurrentWorkspace(data.token, savedWorkspaceId);
            setCurrentWorkspace(workspaceResponse.data);
            setSelectedWorkspaceId(workspaceResponse.data.teamId);
            localStorage.setItem('selected_workspace_id', workspaceResponse.data.teamId);
          } catch (error) {
            console.error('Failed to fetch workspace info:', error);
          }

          setLoading(false);
          return;
        }
      } catch (error) {
        console.error('Failed to check auth status from backend:', error);
      }

      // Fallback: check localStorage (for backwards compatibility)
      const localToken = localStorage.getItem('slack_user_token');
      if (localToken) {
        setUserToken(localToken);
        setIsAuthenticated(true);
      }

      setLoading(false);
    };

    checkAuth();

    // Cleanup event listener
    return () => {
      window.removeEventListener('auth-failed', handleAuthFailed);
    };
  }, []);

  const login = (token) => {
    localStorage.setItem('slack_user_token', token);
    setUserToken(token);
    setIsAuthenticated(true);
  };

  const switchWorkspace = async (workspace) => {
    setCurrentWorkspace(workspace);
    setSelectedWorkspaceId(workspace.teamId);
    localStorage.setItem('selected_workspace_id', workspace.teamId);
  };

  const logout = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

      // Get HubSpot context
      const hubspotContext = getHubSpotContext();

      // Prepare headers
      const headers = {
        'Content-Type': 'application/json'
      };

      if (hubspotContext?.handoffToken) {
        headers['x-hubspot-handoff'] = hubspotContext.handoffToken;
      }

      // Call backend logout API to disconnect from database
      await fetch(`${backendUrl}/api/logout`, {
        method: 'POST',
        credentials: 'include',
        headers
      });

      console.log('Logout successful - tokens removed from database');
    } catch (error) {
      console.error('Error during logout:', error);
      // Continue with local cleanup even if backend fails
    }

    // Clear local storage
    localStorage.removeItem('slack_user_token');
    localStorage.removeItem('slack_team_id');
    localStorage.removeItem('selected_workspace_id');
    localStorage.removeItem('hubspot_user_id');
    localStorage.removeItem('hubspot_portal_id');
    localStorage.clear();

    // Clear cookies
    document.cookie.split(";").forEach((c) => {
      document.cookie = c
        .replace(/^ +/, "")
        .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });

    // Update state
    setUserToken(null);
    setIsAuthenticated(false);
    setCurrentWorkspace(null);
    setSelectedWorkspaceId(null);

    // Build oauth-launcher URL with HubSpot context. Carry the handoff token so
    // re-login can re-bind to the verified user. (sessionStorage is cleared by
    // localStorage.clear() above only if same storage — handoff lives in
    // sessionStorage, so read it before it may be gone.)
    const hubspotContext = getHubSpotContext();
    let oauthLauncherUrl = '/oauth-launcher.html';
    const params = new URLSearchParams();
    if (hubspotContext?.handoffToken) {
      params.append('st', hubspotContext.handoffToken);
    }
    if (hubspotContext?.userId) {
      params.append('hubspotUserId', hubspotContext.userId);
    }
    if (hubspotContext?.portalId) {
      params.append('hubspotPortalId', hubspotContext.portalId);
    }
    if (params.toString()) {
      oauthLauncherUrl += '?' + params.toString();
    }

    // Redirect directly to oauth-launcher (skip Auth component)
    window.location.href = oauthLauncherUrl;
  };

  return {
    userToken,
    isAuthenticated,
    loading,
    login,
    logout,
    currentWorkspace,
    selectedWorkspaceId,
    switchWorkspace
  };
};